import React, {
    useEffect, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { UnmountClosed } from 'react-collapse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import exact from 'prop-types-exact';
import A1GA from '../../../helpers/A1GA';
import Button from '../../../common/button/Button';
import Spinner from '../../../common/Spinner/Spinner';

const SpotModal = ({ spotModalOpen, isLoadingSpotData, spotData, spotSlug,
    getSpotDataIfNeeded, setSpotModalOpen }) => {
    const [currentSection, setCurrentSection] = useState('transcript');

    const clientName = spotData.spotClientName || spotData.clientName || 'Antena 1 Client';

    const _logToGA = useCallback((logType) => {
        let action;
        let label;

        if (logType === 'modalOpen') {
            action = 'spot_modal_opened';

            label = `client: ${clientName}, spot: ${spotData.name}`;
        } else if (logType === 'spotLinkClicked') {
            action = 'spot_link_clicked';

            label = `client: ${clientName}, spot: ${spotData.name}, link: ${spotData.link}`;
        } else if (logType === 'clientLinkClicked') {
            action = 'client_link_clicked';

            label = `client: ${clientName}, spot: ${spotData.name}, link: ${spotData.clientLink}`;
        } else if (logType === 'audioPlay') {
            action = 'spot_audio_play';

            label = `client: ${clientName}, spot: ${spotData.name}`;
        }

        A1GA.event({
            category: 'Spots',
            action,
            label,
        });
    }, [clientName, spotData]);

    useEffect(() => {
        if (spotModalOpen) {
            getSpotDataIfNeeded(spotSlug);

            setCurrentSection('transcript'); // reset the section when the slug changes

            if (spotSlug) {
                // This is the case once the (new) spot data was fetched
                _logToGA('modalOpen');
            }
        }
    }, [_logToGA, getSpotDataIfNeeded, spotModalOpen, spotSlug]);

    let imageSrc = spotData.imageFile ?
        `https://a1media.antena1.com.br/image/clientes/400x400/${spotData.clientImageFile}` :
        'https://a1media.antena1.com.br/image/comerciais/400x400/defaultSpot.png';

    if (!spotData.imageFile && spotData.clientImageFile) {
        // If no cover is given for the spot, check if there is one from the client...
        imageSrc = `https://a1media.antena1.com.br/image/clientes/400x400/${spotData.clientImageFile}`;
    }

    // const spotSrc = spotData.get('audioFile') ? `https://a1media.antena1.com.br/audio/comerciais/${spotData.get('audioFile')}` : false;
    // const currentMusicSrc = currentSources.get(0) && currentSources.get(0).get('src');
    // If the player is currently playing or loading, check if the src of the spot matches the currentSources
    // const faIcon = (currentMusicSrc === spotSrc && (playerStatus === 'playing' || playerStatus === 'loading')) ? faPause : faPlay;

    /*
    const _playPauseSpot = useCallback(() => {
        if ((playerStatus === 'playing') && (currentMusicSrc === spotSrc)) {
            // If the user clicks on the same spot while it is playing, pause it
            return pauseMediaPlayer();
        }

        if ((playerStatus === 'paused') && (currentMusicSrc === spotSrc)) {
            // If the user clicks on the same spot while it is paused, resume it
            return playMediaPlayer();
        }

        // otherwise start the (new) spot
        const meta = {
            artist: clientName,
            cover: imageSrc.replace('100x100', '1000x1000'),
            song: spotData.get('name'),
        };
        const sources = {
            src: spotSrc,
            type: 'audio/mp3',
        };

        _logToGA('audioPlay');

        return playMediaFromSourceWithMeta(sources, meta);
    }, [_logToGA, playerStatus, currentMusicSrc, spotSrc, clientName, imageSrc,
        spotData, pauseMediaPlayer, playMediaPlayer, playMediaFromSourceWithMeta]);
    */

    return (
        <ReactModal
            isOpen={spotModalOpen}
            onRequestClose={() => setSpotModalOpen(false)}
            contentLabel="Spot Modal"
            closeTimeoutMS={250}
            overlayClassName="spotModalOverlay"
            className="spotModalContainer"
            shouldFocusAfterRender={false}
        >

            {
                isLoadingSpotData ?
                    <div className="spotModalWithLoadingSpot">
                        <Button title="fechar" className="spotModalCloseButton" onClick={() => setSpotModalOpen(false)}>
                            <FontAwesomeIcon icon={faTimes} className="closeButton" />
                        </Button>
                    </div>
                    :
                    <div className="spotModalWithLoadedSpot">
                        <div className="spotModalWithLoadedSpotContent">
                            <h2 className="">
                                {clientName}
                            </h2>
                            <p className="">
                                {spotData.name}
                            </p>
                        </div>

                        <Button title="fechar" className="spotModalCloseButton" onClick={() => setSpotModalOpen(false)}>
                            <svg className="closeButton" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14.7802 13.719C14.8499 13.7887 14.9052 13.8714 14.9429 13.9625C14.9806 14.0535 15 14.1511 15 14.2496C15 14.3482 14.9806 14.4457 14.9429 14.5368C14.9052 14.6278 14.8499 14.7105 14.7802 14.7802C14.7105 14.8499 14.6278 14.9052 14.5368 14.9429C14.4457 14.9806 14.3482 15 14.2496 15C14.1511 15 14.0535 14.9806 13.9625 14.9429C13.8714 14.9052 13.7887 14.8499 13.719 14.7802L7.5 8.56025L1.28097 14.7802C1.14025 14.9209 0.949387 15 0.750375 15C0.551363 15 0.360502 14.9209 0.21978 14.7802C0.0790571 14.6395 3.923e-09 14.4486 0 14.2496C-3.923e-09 14.0506 0.0790571 13.8598 0.21978 13.719L6.43975 7.5L0.21978 1.28097C0.0790571 1.14025 0 0.949387 0 0.750375C0 0.551363 0.0790571 0.360502 0.21978 0.21978C0.360502 0.0790571 0.551363 0 0.750375 0C0.949387 0 1.14025 0.0790571 1.28097 0.21978L7.5 6.43975L13.719 0.21978C13.8598 0.0790571 14.0506 -3.923e-09 14.2496 0C14.4486 3.923e-09 14.6395 0.0790571 14.7802 0.21978C14.9209 0.360502 15 0.551363 15 0.750375C15 0.949387 14.9209 1.14025 14.7802 1.28097L8.56025 7.5L14.7802 13.719Z" fill="black" />
                            </svg>
                        </Button>
                    </div>
            }
            {
                isLoadingSpotData ?
                    <Spinner /> :
                    !spotData.name ?
                        <div className="spotModalContent">
                            Spot não encontrado
                        </div> :
                        <div className="spotModalContent">
                            <div className="spotModalHeader">
                                <img className="spotModalImage" src={imageSrc} alt={`Capa do spot ${spotData.name}`} />
                            </div>
                            <div className="spotModalInfos">
                                {/* spotData.get('locations').size > 0 &&
                                <React.Fragment>
                                    <h3>
                                        <Button
                                            title="Mostrar as praças"
                                            className="spotModalInfoButton"
                                            onClick={() => setCurrentSection(currentSection === 'locations' ? false : 'locations')}
                                        >
                                            <span>
                                                {`Praça${spotData.get('locations').size > 1 ? 's' : ''}`}
                                            </span>
                                            <FontAwesomeIcon icon={faCaretDown} className="arrowRightDown" rotation={currentSection === 'locations' ? 180 : 0} />
                                        </Button>
                                    </h3>
                                    <UnmountClosed isOpened={currentSection === 'locations'}>
                                        <div className="locationsContainer">
                                            {
                                                // eslint-disable-next-line react/jsx-one-expression-per-line
                                                spotData.get('locations').map((location) => <div key={location} className="locationsItem">{location}</div>)
                                            }
                                        </div>
                                    </UnmountClosed>
                                </React.Fragment> */}
                                {spotData.transcript &&
                                    <React.Fragment>
                                        <h3>
                                            {/*
                                        <Button
                                            title="Mostrar o transcrito"
                                            className="spotModalInfoButton"
                                            onClick={() => setCurrentSection(currentSection === 'transcript' ? false : 'transcript')}
                                        >
                                            <FontAwesomeIcon icon={faCaretDown} className="arrowRightDown" rotation={currentSection === 'transcript' ? 180 : 0} />
                                        </Button>
                                        */}
                                            Transcrito
                                        </h3>
                                        <UnmountClosed isOpened={currentSection === 'transcript'}>
                                            <div
                                                className="spotModalInfoContent"
                                                // eslint-disable-next-line react/no-danger
                                                dangerouslySetInnerHTML={{ __html: spotData.transcript }}
                                            />
                                            {/*
                                            spotData.get('link') &&
                                            <Button
                                                className="spotModalExtraInfoButton"
                                                title="Saiba mais sobre esse spot"
                                                href={spotData.get('link')}
                                                onClick={() => {
                                                    _logToGA('spotLinkClicked');

                                                    setSpotModalOpen(false);
                                                }}
                                            >
                                                Saiba mais
                                            </Button>
                                        */}
                                        </UnmountClosed>
                                    </React.Fragment>}
                                {/* spotData.get('clientDescription') &&
                                <React.Fragment>
                                    <h3>
                                        <Button
                                            title="Mostrar a descrição"
                                            className="spotModalInfoButton"
                                            onClick={() => setCurrentSection(currentSection === 'description' ? false : 'description')}
                                        >
                                            <span>
                                                Informações adicionais
                                            </span>
                                            <FontAwesomeIcon icon={faCaretDown} className="arrowRightDown" rotation={currentSection === 'description' ? 180 : 0} />
                                        </Button>
                                    </h3>
                                    <UnmountClosed isOpened={currentSection === 'description'}>
                                        <div
                                            className="spotModalInfoContent"
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{ __html: spotData.get('clientDescription') }}
                                        />
                                        {
                                            spotData.get('clientLink') &&
                                            <Button
                                                className="spotModalExtraInfoButton"
                                                title="Saiba mais sobre esse cliente"
                                                href={spotData.get('clientLink')}
                                                onClick={() => {
                                                    _logToGA('clientLinkClicked');

                                                    setSpotModalOpen(false);
                                                }}
                                            >
                                                Saiba mais
                                            </Button>
                                        }
                                    </UnmountClosed>
                                </React.Fragment> */}
                                {/* spotSrc &&
                                <React.Fragment>
                                    <h3>
                                        <Button
                                            title="Mostrar o Áudio"
                                            className="spotModalInfoButton"
                                            onClick={() => setCurrentSection(currentSection === 'audio' ? false : 'audio')}
                                        >
                                            <span>
                                                Áudio
                                            </span>
                                            <FontAwesomeIcon icon={faCaretDown} className="arrowRightDown" rotation={currentSection === 'audio' ? 180 : 0} />
                                        </Button>
                                    </h3>
                                    <UnmountClosed isOpened={currentSection === 'audio'}>
                                        <div className="spotModalInfoAudio">
                                            <Button className="spotModalInfoPlayButton" title="Toque o audio desse spot" onClick={() => _playPauseSpot()}>
                                                <FontAwesomeIcon icon={faIcon} />
                                            </Button>
                                            <div className="spotModalInfoContent">
                                                {`Tocar o spot "${spotData.get('name')}"`}
                                            </div>
                                        </div>
                                    </UnmountClosed>
                                </React.Fragment> */}

                                {/*
                                    <h3>
                                        <Button
                                            title="Mostrar a como anunciar na Antena 1"
                                            className="spotModalInfoButton"
                                            onClick={() => setCurrentSection(currentSection === 'advertise' ? false : 'advertise')}
                                        >
                                            <span>
                                                Anuncie conosco
                                            </span>
                                            <FontAwesomeIcon icon={faCaretDown} className="arrowRightDown" rotation={currentSection === 'advertise' ? 180 : 0} />
                                        </Button>
                                    </h3>
                                    <UnmountClosed isOpened={currentSection === 'advertise'}>
                                        <div className="spotModalInfoContent">
                                            <Button className="spotModalExtraInfoButton advertise" title="Saiba como anunciar conosco" to="/anuncie-conosco" onClick={() => setSpotModalOpen(false)}>
                                                Clique aqui para saber como anunciar conosco
                                            </Button>
                                        </div>
                                    </UnmountClosed>
                                */}
                            </div>
                        </div>
            }
        </ReactModal>
    );
};

SpotModal.propTypes = exact({
    spotData: PropTypes.shape({
        name: PropTypes.string,
        audioFile: PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.string,
        ]),
        imageFile: PropTypes.string,
        transcript: PropTypes.string,
        link: PropTypes.string,
        spotClientName: PropTypes.string,
        clientName: PropTypes.string,
        clientDescription: PropTypes.string,
        clientImageFile: PropTypes.string,
        clientColor: PropTypes.string,
        clientLink: PropTypes.string,
    }).isRequired,
    spotSlug: PropTypes.string.isRequired,
    spotModalOpen: PropTypes.bool.isRequired,
    isLoadingSpotData: PropTypes.bool.isRequired,
    getSpotDataIfNeeded: PropTypes.func.isRequired,
    setSpotModalOpen: PropTypes.func.isRequired,
    // currentSources: ImmutablePropTypes.listOf(
    //    ImmutablePropTypes.contains({
    //        src: PropTypes.string.isRequired,
    //        type: PropTypes.string,
    //    }),
    // ),
    // playerStatus: PropTypes.string.isRequired,
    // pauseMediaPlayer: PropTypes.func.isRequired,
    // playMediaPlayer: PropTypes.func.isRequired,
    // playMediaFromSourceWithMeta: PropTypes.func.isRequired,
});

export default SpotModal;
